import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useFormContext } from 'react-hook-form';
import FormInput from '../../input/FormInput';
import { getScaleReadings } from '../../../../../data/generic-queries/scale-readings';

const UniqueScaleTicketNumber = ({ name, enableAsyncBlock, isEdit, item, disableOnEdit, props: { label, required, ...rest } }) => {
    const { getValues, watch, setError, clearErrors, setAsyncLock } = useFormContext();
    const [isFetching, setIsFetching] = useState(false);

    const ticket = (watch('scale_ticket_number') ?? '').trim();
    const processor = getValues('processor_id');

    const validationRequired = ticket.length > 0 && !!processor;

    const debouncedValidate = useMemo(
        () =>
            debounce(async (ticketNumber, processorId) => {
                setIsFetching(true);
                try {
                    const response = await getScaleReadings({
                        queryParams: {
                            where: {
                                scale_ticket_number: ticketNumber,
                                processor_id: processorId,
                            },
                            limit: 1,
                        },
                    });

                    const readings = response?.data || [];
                    const exists = Array.isArray(readings) && readings.length > 0;
                    const isSameItem = exists && item && readings[0]._id === item._id;

                    if (exists && !isSameItem) {
                        setError(name, {
                            type: 'validate',
                            message: 'Scale ticket number must be unique per processor.',
                        });
                    } else {
                        clearErrors(name);
                    }
                } catch (error) {
                    setError(name, {
                        type: 'validate',
                        message: 'Error validating scale ticket number.',
                    });
                } finally {
                    setIsFetching(false);
                }
            }, 500),
        [name, setError, clearErrors]
    );

    useEffect(() => {
        if (!validationRequired) {
            clearErrors(name);
            return;
        }

        debouncedValidate(ticket, processor);

        return () => {
            debouncedValidate.clear();
        };
    }, [ticket, processor, validationRequired, name, clearErrors, debouncedValidate]);

    useEffect(() => {
        if (!name || !enableAsyncBlock || !setAsyncLock) return;
        setAsyncLock(name, isFetching);
        return () => setAsyncLock?.(name, false);
    }, [name, enableAsyncBlock, isFetching, setAsyncLock]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <FormInput
                    name={name}
                    props={{
                        label,
                        required,
                        ...rest,
                        disabled: isFetching || (disableOnEdit && isEdit),
                        extraHelperText: isFetching ? 'Verifying uniqueness...' : '',
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default UniqueScaleTicketNumber;
